import React, { useCallback, useEffect, useRef, useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { usePermissions } from "../../hooks/usePermissions";
import { CAPABILITY } from "../../helpers/permissions";
import { listAllUsers } from "../../helpers/admin/usersAdmin";
import { AdminEmployeeProfilePanel } from "./panels/AdminEmployeeProfilePanel";
import { AdminManagerToolsPanel } from "./panels/AdminManagerToolsPanel";
import {
  AdminUserDetailPanel,
  AdminUsersDirectoryPanel,
} from "./panels/AdminUsersPanels";
import { useHardwareBackHandler } from "../../navigation/HardwareBackContext";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import "./adminDesktop.css";

/**
 * Maps legacy deep-link props from the deprecated Admin Tools hub to the
 * flattened Users workspace views.
 */
export function resolveUsersHubActiveView(initialActiveView, initialAdminToolMode) {
  if (initialActiveView === "user_management") {
    return "user_management";
  }

  if (initialActiveView === "admin_tools" && initialAdminToolMode === "users") {
    return "user_management";
  }

  if (
    initialActiveView &&
    initialActiveView !== "admin_tools" &&
    initialActiveView !== "menu"
  ) {
    return initialActiveView;
  }

  return "menu";
}

export function AdminUsersHubScreen({
  onBack,
  currentUser,
  initialActiveView,
  initialAdminToolMode,
  useLegacyShell = false,
}) {
  const { can } = usePermissions();
  const canEditReports = can(CAPABILITY.USERS_EDIT_REPORTS);
  const canEditUsers = can(CAPABILITY.USERS_EDIT);
  const touchStartX = useRef(null);
  const isDesktop = useIsDesktopViewport();

  const [activeView, setActiveView] = useState(() =>
    resolveUsersHubActiveView(initialActiveView, initialAdminToolMode),
  );
  const [allUsers, setAllUsers] = useState([]);
  const [selectedAdminUser, setSelectedAdminUser] = useState(null);

  const loadAllUsers = useCallback(async () => {
    if (!currentUser) {
      return;
    }
    try {
      const data = await listAllUsers(currentUser);
      setAllUsers(data);
    } catch (err) {
      console.error("LOAD USERS ERROR:", err);
    }
  }, [currentUser]);

  useEffect(() => {
    if (activeView === "user_management" && canEditUsers) {
      loadAllUsers();
    }
  }, [activeView, canEditUsers, loadAllUsers]);

  const handleHardwareBack = useCallback(() => {
    if (selectedAdminUser) {
      setSelectedAdminUser(null);
      return true;
    }

    if (
      activeView === "employee_profile" ||
      activeView === "manager_tools" ||
      activeView === "user_management"
    ) {
      setActiveView("menu");
      return true;
    }

    return false;
  }, [activeView, selectedAdminUser]);

  useHardwareBackHandler(handleHardwareBack);

  if (activeView === "employee_profile") {
    return (
      <AdminEmployeeProfilePanel
        currentUser={currentUser}
        onBack={() => setActiveView("menu")}
      />
    );
  }

  if (activeView === "manager_tools") {
    return (
      <AdminManagerToolsPanel
        currentUser={currentUser}
        onBack={() => setActiveView("menu")}
      />
    );
  }

  if (selectedAdminUser) {
    return (
      <AdminUserDetailPanel
        user={selectedAdminUser}
        allUsers={allUsers}
        currentUser={currentUser}
        onBack={() => setSelectedAdminUser(null)}
        onSaved={(updated) => {
          setSelectedAdminUser(updated);
          loadAllUsers();
        }}
      />
    );
  }

  if (activeView === "user_management") {
    return (
      <AdminUsersDirectoryPanel
        showLegacyHeader
        currentUser={currentUser}
        onBack={() => setActiveView("menu")}
        onSelectUser={(user) => {
          setSelectedAdminUser(user);
        }}
      />
    );
  }

  const desktopEntries = [
    {
      key: "employee_profile",
      icon: "👤",
      title: "Employee Profile",
      sub: "Manage personal information",
      show: true,
    },
    {
      key: "manager_tools",
      icon: "👥",
      title: "Manager Tools",
      sub: "Manage employees",
      show: canEditReports,
    },
    {
      key: "user_management",
      icon: "🗂️",
      title: "User Management",
      sub: "Roles and assignments",
      show: canEditUsers,
    },
  ].filter((entry) => entry.show);

  const desktopMenu = (
    <div className="adm-dir">
      {desktopEntries.map((entry) => (
        <button
          key={entry.key}
          type="button"
          className="adm-dir__item"
          onClick={() => setActiveView(entry.key)}
        >
          <span className="adm-dir__icon" aria-hidden="true">
            {entry.icon}
          </span>
          <span className="adm-dir__text">
            <span className="adm-dir__title">{entry.title}</span>
            <span className="adm-dir__sub">{entry.sub}</span>
          </span>
          <span className="adm-dir__chev" aria-hidden="true">
            ›
          </span>
        </button>
      ))}
    </div>
  );

  const menu = isDesktop ? desktopMenu : (
    <div className="quick-grid">
      <button
        type="button"
        className="quick-card user-management-card"
        onClick={() => setActiveView("employee_profile")}
      >
        <div className="quick-card-icon">👤</div>
        <div className="quick-card-title">Employee Profile</div>
        <div className="quick-card-subtitle">Manage personal information</div>
      </button>

      {(canEditReports) && (
        <button
          type="button"
          className="quick-card user-management-card"
          onClick={() => setActiveView("manager_tools")}
        >
          <div className="quick-card-icon">👥</div>
          <div className="quick-card-title">Manager Tools</div>
          <div className="quick-card-subtitle">Manage employees</div>
        </button>
      )}

      {(canEditUsers) && (
        <button
          type="button"
          className="quick-card user-management-card"
          onClick={() => setActiveView("user_management")}
        >
          <div className="quick-card-icon">🗂️</div>
          <div className="quick-card-title">User Management</div>
          <div className="quick-card-subtitle">Roles and assignments</div>
        </button>
      )}
    </div>
  );

  if (useLegacyShell) {
    return (
      <div
        onTouchStart={(e) => {
          touchStartX.current = e.touches[0].clientX;
        }}
        onTouchEnd={(e) => {
          if (touchStartX.current === null) return;

          const deltaX = e.changedTouches[0].clientX - touchStartX.current;

          if (Math.abs(deltaX) > 80) {
            onBack?.();
          }

          touchStartX.current = null;
        }}
      >
        <CrewPilotModuleShell
          title="User Management"
          subtitle="Employee and role administration"
          sectionLabel="Select Module"
          onBack={null}
          layout={PAGE_LAYOUT.COMMAND}
        >
          {menu}
        </CrewPilotModuleShell>
      </div>
    );
  }

  return (
    <CrewPilotModuleShell
      title="Users"
      subtitle="Employee profiles, manager tools, and user directory"
      sectionLabel="Administration"
      onBack={onBack}
      layout={PAGE_LAYOUT.COMMAND}
    >
      {menu}
    </CrewPilotModuleShell>
  );
}
